import type { RequestProfile } from '../../../types/Configuration';
import { useParams } from 'react-router-dom';
import useAppConfig from './useAppConfig';
import useUrlInfo from '../../libs/utils/urls/useUrlInfo';
import useExperience from '../experience/useExperience';

const useChannelSource = () => {
  const { config } = useAppConfig().states;
  const { channelSourceApiKey } = useParams();
  const { addChannelSource, overrideChannelSource } = useUrlInfo().states;
  const { experience } = useExperience().states;

  const convertApiKeyToRequestProfile = (apiKey: string): RequestProfile | undefined =>
    config.requestProfiles?.find((profile) => profile.apiKey === apiKey);

  const profileChannelSource = channelSourceApiKey
    ? convertApiKeyToRequestProfile(channelSourceApiKey)?.channelSource
    : undefined;

  const resolveChannelSource = () => {
    const primary = overrideChannelSource ?? profileChannelSource ?? experience?.channelSource ?? undefined;

    return {
      primary: primary,
      secondary: addChannelSource ?? undefined,
      formatted: `${primary}${addChannelSource ? '+' + addChannelSource : ''}`,
    };
  };

  const isMatchingChannelSourceAnyOf = (channelSourcesAnyOf?: string[], channelSource?: string) => {
    if (!channelSourcesAnyOf) return true;

    if (channelSource) {
      return channelSourcesAnyOf.includes(channelSource);
    }

    const { primary, secondary } = resolveChannelSource();
    return (
      (primary !== undefined && channelSourcesAnyOf.includes(primary)) ||
      (secondary !== undefined && channelSourcesAnyOf.includes(secondary))
    );
  };

  return {
    actions: {
      convertApiKeyToRequestProfile,
    },
    states: {
      channelSourceApiKey,
      channelSource: resolveChannelSource(),
    },
    selectors: {
      isMatchingChannelSourceAnyOf,
      getChannelSourceByKey: (channelSourceKey: string) =>
        config.requestProfiles?.find((profile) => profile.channelSource === channelSourceKey)?.channelSource,
    },
  };
};

export default useChannelSource;
